/**
 * Whether a learner may state another objective, and what to tell them when they may not.
 *
 * **The screen has to know this, and until 2026-09-19 nothing did.** The create form rendered
 * only for a learner with no objective at all, which was right while the slot cap was one and
 * became the reason a second plan could not be created on the day the cap went to three: the API
 * would have accepted it, and no screen offered it. The owner found it by looking for the tabs
 * and finding no way to earn them.
 *
 * Pure, and here rather than in the component, because the interesting part is what happens when
 * the answer is not known — and a branch nothing can test is how the current bug survived.
 */

/** What the plan screen needs to decide, which is less than an entitlement. */
export interface Room {
  /** Live plans right now. `undefined` while they are still being read. */
  live: number | undefined;
  /** Slots the tier grants, from `/api/auth/me`. `undefined` when that read failed. */
  cap: number | undefined;
}

export type Offer =
  /** Render the form. */
  | { offer: true; remaining: number | undefined }
  /** They are at the cap: say so, and say what to do about it. */
  | { offer: false; reason: 'at_cap'; cap: number }
  /** Still loading. Render nothing rather than flicker a form away. */
  | { offer: false; reason: 'unknown' };

/**
 * **Degrades towards offering, and that direction is the decision.**
 *
 * When the cap cannot be read — `/api/auth/me` failed, an older browser, a field that is not
 * there yet — the form is shown. The two ways to be wrong are not equal: offering a form to
 * somebody at their cap costs them one refusal with copy that explains it, and *hiding* it costs
 * them a feature they are paying for with no way to discover it. The second is the bug this file
 * exists to close, and a cautious default would have reproduced it exactly.
 *
 * `live === undefined` is the one case that shows nothing, because it means the plans have not
 * arrived yet — and a form that appears and then vanishes is worse than one that arrives a beat
 * late.
 */
export function roomForAnother({ live, cap }: Room): Offer {
  if (live === undefined) return { offer: false, reason: 'unknown' };
  if (cap === undefined) return { offer: true, remaining: undefined };
  if (live >= cap) return { offer: false, reason: 'at_cap', cap };
  return { offer: true, remaining: cap - live };
}
